import {
  getSrmMaterialSapList,
  type MaterialSapVo,
  type SrmMaterialSapPageDto,
} from "@/apis/sap.api";
import BaseProtable from "@/components/BaseProTable";
import { PrimaryButton } from "@/components/ui/buttons/PrimaryButton";
import CommonModal from "@/components/ui/CommonModal";
import { notifyWarning } from "@/utils/notification";
import { type TableProps } from "antd";
import { useEffect, useState } from "react";
import styles from "./index.module.less";
import { usePageTable } from "@/hooks/baseProTable/usePageTable";
import TextEllipsis from "../ui/TextEllipsis";

type Props = {
  open: boolean;
  onOk: (materials: MaterialSapVo[]) => void;
  onClose: () => void;
  /** 初始过滤参数，会在弹窗打开时自动填充并查询 */
  initialParams?: Partial<SrmMaterialSapPageDto>;
  /** 选择模式，默认多选 */
  selectionMode?: "multiple" | "single";
};

const SelectMaterialModal: React.FC<Props> = ({
  open,
  onOk,
  onClose,
  initialParams,
  selectionMode = "multiple",
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<MaterialSapVo[]>([]);
  const [params, setParams] = useState<SrmMaterialSapPageDto>({});

  const { actionRef, fetchDatasource, columns, formRef } = usePageTable<
    MaterialSapVo,
    SrmMaterialSapPageDto
  >({
    fetchFn: getSrmMaterialSapList,
    columns: [
      {
        title: "序号",
        valueType: "index",
        width: 60,
        align: "center",
        fixed: "left",
      },
      { title: "物料编号", dataIndex: "f101", width: 150, align: "center" },
      {
        title: "物料名称",
        dataIndex: "keyword",
        align: "center",
        hideInTable: true,
      },
      {
        title: "物料中文描述",
        dataIndex: "f102",
        align: "center",
        search: false,
      },
      {
        title: "物料英文描述",
        dataIndex: "f103",
        align: "center",
        search: false,
      },
      {
        title: "相关产品",
        dataIndex: "f131",
        align: "left",
        search: false,
        width: 200,
        render: (_, record) => <TextEllipsis text={record.f131 || "-"} />,
      },
    ],
  });

  // 当弹窗打开时，如果传入了初始参数，则设置到查询参数中
  useEffect(() => {
    if (open && initialParams) {
      setParams(initialParams);
      // 使用 setTimeout(0) 确保表单已渲染完成
      const timer = setTimeout(() => {
        formRef.current?.setFieldsValue({ f101: initialParams.f101 });
        actionRef.current?.reload();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open, initialParams, actionRef, formRef]);

  const rowSelection: TableProps<MaterialSapVo>["rowSelection"] = {
    type: selectionMode === "single" ? "radio" : "checkbox",
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      if (selectionMode === "single") {
        setSelectedRowKeys(selectedRowKeys.slice(0, 1));
        setSelectedRows(selectedRows.slice(0, 1));
        return;
      }
      setSelectedRowKeys(selectedRowKeys);
      setSelectedRows(selectedRows);
    },
  };

  const handleOk = () => {
    if (selectedRows.length === 0) {
      notifyWarning("请选择物料");
      return;
    }
    onOk?.(selectedRows);
    handleClose();
  };

  const handleClose = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setParams({});
    onClose();
  };

  const footer = () => (
    <div style={{ textAlign: "center" }}>
      <PrimaryButton onClick={handleOk}>确定</PrimaryButton>
    </div>
  );

  return (
    <CommonModal
      title="选择物料"
      open={open}
      onCancel={handleClose}
      width={1200}
      style={{ top: 20 }}
      footer={footer}
    >
      <BaseProtable<MaterialSapVo>
        headerTitle={null}
        actionRef={actionRef}
        formRef={formRef}
        columns={columns}
        className={styles.modalProTable}
        rowSelection={rowSelection}
        rowKey="f101"
        bordered
        request={fetchDatasource}
        params={params}
        search={{
          labelWidth: "auto",
          span: 6,
        }}
        onSubmit={(values) => setParams({ ...values })}
      />
    </CommonModal>
  );
};

export default SelectMaterialModal;
